import { IconLayer, TextLayer } from '@deck.gl/layers/typed';
import { Color, Layer } from '@deck.gl/core/typed';
import { PlannerMapDataElement } from 'components/common/types/Planner.types';
import { Position } from '@deck.gl/core/typed/types/layer-props';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import { UsePlannerSegmentsIndexRange } from '../../hooks/usePlannerActions.types';
import { createSVGData } from '../utils/createSVGData';
import { MAP_ELEMENT_RADIUS_SCALE } from '../utils/getMapElementSize';
import { ClusterState, getClusterState } from './utils';

const ICON_RESOLUTION = 64;
const MARKER_FILLING_ICON_RESOLUTION = 128;
const ICON_SIZE_RATIO = 0.5;
const MIN_ICON_SIZE = 10;
const MAX_ICON_SIZE = 44;
const TEXT_FONT_SIZE = 12;
const TEXT_COLOR: Color = [255, 255, 255];
const TEXT_FONT = 'Inter';
const MAP_MARKER_ICON = {
  width: MARKER_FILLING_ICON_RESOLUTION,
  height: MARKER_FILLING_ICON_RESOLUTION,
  url: createSVGData(`
        <svg width="${MARKER_FILLING_ICON_RESOLUTION}" height="${MARKER_FILLING_ICON_RESOLUTION}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" fill-rule="evenodd">
                  <path d="M10 21l7.5 7.5 12.5-15" stroke="#FBFBFB" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
        </svg>`),
};
export const MAP_WARNING_ICON = {
  width: ICON_RESOLUTION,
  height: ICON_RESOLUTION,
  url: createSVGData(`
        <svg width="${ICON_RESOLUTION}" height="${ICON_RESOLUTION}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <g fill="none" fill-rule="evenodd">
                <path d="M20 8l14 24H6L20 8z" stroke="#FBFBFB" stroke-width="3" stroke-linejoin="round"/>
                <path d="M20 15v8" stroke="#FBFBFB" stroke-width="3" stroke-linecap="round"/>
                <circle cx="20" cy="27.5" r="1.5" fill="#FBFBFB"/>
            </g>
        </svg>`),
};

const PARTIAL_SELECTION_STEPS = 12;
const PARTIAL_SELECTION_WEDGE_RADIUS = 20;

type MapIcon = { width: number; height: number; url: string };

const partialSelectionIcons = new Map<number, MapIcon>();

const getPartialSelectionIcon = (selectedRatio: number): MapIcon => {
  const step = Math.min(Math.max(Math.round(selectedRatio * PARTIAL_SELECTION_STEPS), 1), PARTIAL_SELECTION_STEPS - 1);
  const cachedIcon = partialSelectionIcons.get(step);
  if (cachedIcon) return cachedIcon;

  const sweep = (step / PARTIAL_SELECTION_STEPS) * 2 * Math.PI;
  const endX = (20 + PARTIAL_SELECTION_WEDGE_RADIUS * Math.sin(sweep)).toFixed(2);
  const endY = (20 - PARTIAL_SELECTION_WEDGE_RADIUS * Math.cos(sweep)).toFixed(2);
  const largeArcFlag = (PARTIAL_SELECTION_STEPS - step) * 2 > PARTIAL_SELECTION_STEPS ? 1 : 0;

  const icon = {
    width: MARKER_FILLING_ICON_RESOLUTION,
    height: MARKER_FILLING_ICON_RESOLUTION,
    url: createSVGData(`
        <svg width="${MARKER_FILLING_ICON_RESOLUTION}" height="${MARKER_FILLING_ICON_RESOLUTION}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
            <g fill-rule="evenodd">
                <path d="M20 20 L${endX} ${endY} A${PARTIAL_SELECTION_WEDGE_RADIUS} ${PARTIAL_SELECTION_WEDGE_RADIUS} 0 ${largeArcFlag} 1 20 ${20 - PARTIAL_SELECTION_WEDGE_RADIUS} Z" fill="#FBFBFB"/>
            </g>
        </svg>`),
  };

  partialSelectionIcons.set(step, icon);

  return icon;
};

type CreateMapMarkerIconWithTextLayerProps = (
  mapData: PlannerMapDataElement[],
  routeFrameCodes: RouteFrameCode[],
  getPosition: (data: PlannerMapDataElement) => Position,
  updateTriggers: Function,
  getTextRGBColorByIndexValue: UsePlannerSegmentsIndexRange['getTextRGBColorByIndexValue'],
  getMapElementSize: (data: PlannerMapDataElement) => number,
  shouldShowSelectionState: boolean,
) => Layer[];

export const createMapMarkerIconWithTextLayer: CreateMapMarkerIconWithTextLayerProps = (
  mapData,
  routeFrameCodes,
  getPosition,
  updateTriggers,
  getTextRGBColorByIndexValue,
  getMapElementSize,
  shouldShowSelectionState,
): Layer[] => {
  const clusterStates = new WeakMap<PlannerMapDataElement, ClusterState>();

  const getCachedClusterState = (data: PlannerMapDataElement): ClusterState => {
    const cachedState = clusterStates.get(data);
    if (cachedState) return cachedState;

    const clusterState = getClusterState(data, routeFrameCodes);
    clusterStates.set(data, clusterState);

    return clusterState;
  };

  const getMarkerDiameter = (data: PlannerMapDataElement): number =>
    getMapElementSize(data) * MAP_ELEMENT_RADIUS_SCALE * 2;

  const getIconSize = (data: PlannerMapDataElement): number => {
    const markerDiameter = getMarkerDiameter(data);
    if (!markerDiameter) return 0;

    return Math.min(Math.max(markerDiameter * ICON_SIZE_RATIO, MIN_ICON_SIZE), MAX_ICON_SIZE);
  };

  const shouldShowPartialSelectionPie = (data: PlannerMapDataElement, clusterState: ClusterState): boolean =>
    shouldShowSelectionState &&
    clusterState.isPartiallySelected &&
    !clusterState.isPartiallyMissing &&
    !clusterState.isEveryMissing &&
    Boolean(data.properties.isSingleLocation);

  const getIconTextValue = (data: PlannerMapDataElement): string => {
    if (!data.properties.cluster) return '';

    const clusterState = getCachedClusterState(data);
    if (
      shouldShowSelectionState &&
      clusterState.isPartiallySelected &&
      !shouldShowPartialSelectionPie(data, clusterState)
    )
      return `${clusterState.selectedCount}`;

    return `${data.properties.point_count}`;
  };

  const getClusterTextColor = (data: PlannerMapDataElement): Color => {
    if (!data.index) return TEXT_COLOR;

    const textColorByIndexValue = getTextRGBColorByIndexValue(data.index);

    return textColorByIndexValue ? (textColorByIndexValue as Color) : TEXT_COLOR;
  };

  const getWarningIconSize = (data: PlannerMapDataElement): number => {
    const { isEveryMissing, isPartiallyMissing } = getCachedClusterState(data);
    if (!isPartiallyMissing && !isEveryMissing) return 0;

    return getIconSize(data);
  };

  const getCheckmarkIconSize = (data: PlannerMapDataElement): number => {
    const { isEverySelected, isEveryMissing, isPartiallyMissing } = getCachedClusterState(data);
    if (!isEverySelected || isPartiallyMissing || isEveryMissing) return 0;

    return getMarkerDiameter(data);
  };

  const getPartialSelectionIconSize = (data: PlannerMapDataElement): number =>
    shouldShowPartialSelectionPie(data, getCachedClusterState(data)) ? getMarkerDiameter(data) : 0;

  const getPartialSelectionRatio = (data: PlannerMapDataElement): number =>
    getCachedClusterState(data).selectedCount / (data.properties.assets?.length || 1);

  const getTextSize = (data: PlannerMapDataElement): number => {
    const clusterState = getCachedClusterState(data);
    const { isEverySelected, isEveryMissing, isPartiallyMissing } = clusterState;
    if (isEverySelected || isPartiallyMissing || isEveryMissing || shouldShowPartialSelectionPie(data, clusterState))
      return 0;

    return TEXT_FONT_SIZE;
  };

  return [
    new IconLayer({
      id: 'map-marker-warning-icon-layer',
      data: mapData,
      iconSizeScale: 1,
      sizeScale: 1,
      sizeUnits: 'pixels',
      getPosition,
      getIcon: () => MAP_WARNING_ICON,
      getSize: getWarningIconSize,
      updateTriggers,
    }),
    new IconLayer({
      id: 'map-marker-icon-layer',
      data: mapData,
      iconSizeScale: 1,
      sizeScale: 1,
      sizeUnits: 'pixels',
      getPosition,
      getIcon: () => MAP_MARKER_ICON,
      getSize: getCheckmarkIconSize,
      updateTriggers,
    }),
    new IconLayer({
      id: 'map-marker-partial-selection-icon-layer',
      data: mapData,
      iconSizeScale: 1,
      sizeScale: 1,
      sizeUnits: 'pixels',
      getPosition,
      getIcon: (data: PlannerMapDataElement) => getPartialSelectionIcon(getPartialSelectionRatio(data)),
      getSize: getPartialSelectionIconSize,
      updateTriggers,
    }),
    new TextLayer({
      id: 'map-marker-text-layer',
      data: mapData,
      sizeUnits: 'pixels',
      fontFamily: TEXT_FONT,
      fontWeight: 'bold',
      getColor: getClusterTextColor,
      getSize: getTextSize,
      getText: getIconTextValue,
      getPosition,
      updateTriggers,
    }),
  ];
};
